import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dashboardUrl } from '../../config/app';
import { cachedApiRequest, getPaginatedRows } from '../../lib/api';
import {
  SAMPLE_TEMPLATES,
  forceDesktopPreview,
  normalizeTemplate,
  templateIdFromParam,
  templatePath,
  templateSeoCopy,
} from './templateData';
import { metaForPath } from '../../seo/seo';
import SEO from '../SEO/SEO';
import './TemplateDetail.css';

function downloadHtmlTemplate(template) {
  const html = template.html || `<!doctype html><html><head><meta charset="utf-8"><title>${template.name}</title></head><body><h1>${template.name}</h1></body></html>`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${String(template.name || 'email-template').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.html`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function TemplateDetail({ initialData }) {
  const { templateId } = useParams();
  const resolvedTemplateId = templateIdFromParam(templateId);
  const [template, setTemplate] = useState(() => initialData?.template || null);
  const [loading, setLoading] = useState(() => !initialData);
  const skipInitialFetch = useRef(Boolean(initialData?.template));

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return undefined;
    }

    let cancelled = false;

    async function loadTemplate() {
      setLoading(true);
      try {
        const data = await cachedApiRequest(`/public/email-templates/${encodeURIComponent(resolvedTemplateId)}`, {
          cacheKey: `public-email-template-${resolvedTemplateId}`,
        });
        if (!cancelled) setTemplate(normalizeTemplate(data.data || data));
      } catch {
        try {
          const data = await cachedApiRequest('/public/email-templates?per_page=100', {
            cacheKey: 'public-email-templates-catalog',
          });
          const rows = getPaginatedRows(data);
          const match = rows.map(normalizeTemplate).find((item) => String(item.id) === String(resolvedTemplateId));
          if (!cancelled) setTemplate(match || null);
        } catch {
          if (!cancelled) {
            const fallback = SAMPLE_TEMPLATES.find((item) => String(item.id) === String(resolvedTemplateId));
            setTemplate(fallback || null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [resolvedTemplateId]);

  const previewHtml = useMemo(() => forceDesktopPreview(template?.html || ''), [template]);

  const handleEdit = () => {
    if (!template) return;
    window.location.href = dashboardUrl(`/templates/editor/new?template_id=${encodeURIComponent(template.id)}`);
  };

  if (loading) {
    return (
      <main className="template-detail-page">
        <SEO
          meta={metaForPath(`/templates/${templateId}`)}
        />
        <section className="template-detail-loading">Loading template preview...</section>
      </main>
    );
  }

  if (!template) {
    return (
      <main className="template-detail-page">
        <SEO
          meta={{
            path: `/templates/${templateId}`,
            title: 'Template Not Found | Mailsnappy',
            description: 'This Mailsnappy email template could not be found. Browse the full email template catalog instead.',
            h1: 'Template not found',
            robots: 'noindex, nofollow',
            breadcrumbs: [
              { name: 'Home', path: '/' },
              { name: 'Email templates', path: '/templates' },
              { name: 'Template not found', path: `/templates/${templateId}` },
            ],
          }}
        />
        <section className="template-detail-empty">
          <h1>Template not found</h1>
          <p>This template may have moved, but the full catalog is ready for you.</p>
          <Link to="/templates">Back to templates</Link>
        </section>
      </main>
    );
  }

  const seoCopy = templateSeoCopy(template);

  return (
    <main className="template-detail-page">
      <SEO
        meta={{
          path: templatePath(template),
          title: `${template.name} Email Template | Mailsnappy`,
          description: `Preview and download the ${template.name} email template for responsive newsletters, promotions, announcements, and marketing campaigns.`,
          h1: template.name,
          robots: 'index, follow',
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Email templates', path: '/templates' },
            { name: template.name, path: templatePath(template) },
          ],
          schema: ['WebApplication'],
        }}
      />
      <section className="template-detail-hero">
        <div>
          <Link className="template-back-link" to="/templates">Back to templates</Link>
          <span className="template-detail-eyebrow">{template.category} template</span>
          <h1>{template.name}</h1>
          <p>
            Preview the full {template.name} email template below. This template is free to download,
            customize, and use for your next campaign.
          </p>
          <div className="template-detail-actions">
            <button type="button" onClick={handleEdit}>Edit template</button>
            <button type="button" className="secondary" onClick={() => downloadHtmlTemplate(template)}>
              Download template
            </button>
          </div>
        </div>
        <aside className="template-free-card">
          <strong>Free template download</strong>
          <p>
            Download {template.name} as an HTML email template, then edit it in Mailsnappy or keep it
            for your own campaign workflow.
          </p>
        </aside>
      </section>

      <section className="template-detail-preview-section">
        <div className="template-detail-preview-header">
          <div>
            <h2>Full template preview</h2>
            <p>{template.name} is ready to customize with your copy, branding, links, and campaign content.</p>
          </div>
          <button type="button" onClick={handleEdit}>Edit this design</button>
        </div>

        <div className="template-detail-preview-shell">
          {previewHtml ? (
            <iframe title={`${template.name} full template preview`} srcDoc={previewHtml} />
          ) : template.previewImage ? (
            <img src={template.previewImage} alt={`${template.name} preview`} />
          ) : (
            <div className="template-detail-no-preview">No preview available.</div>
          )}
        </div>
      </section>

      <section className="template-detail-copy">
        <h2>Download {template.name} for free</h2>
        <p>
          Use this free {template.category.toLowerCase()} email template to move faster from idea to send.
          Open it in the editor to adjust layout sections, update colors, replace text, and prepare a
          polished campaign without starting from a blank page.
        </p>
        <h2>{template.name} use cases</h2>
        <p>{seoCopy.intro}</p>
        <p>{seoCopy.details}</p>
        <ul>
          {seoCopy.checklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

    </main>
  );
}
